import { ENVIRONMENT } from '@us/app-config';
import { AsyncPipe, CurrencyPipe, NgIf, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, Input, OnInit } from '@angular/core';
import { ModalController, NavController } from '@ionic/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TuiLetModule } from '@taiga-ui/cdk';
import { TuiButtonModule, TuiDialogService } from '@taiga-ui/core';
import {
    CartActions,
    CartItem,
    selectItemInCart,
    selectItemQuantityInCart,
    selectSelectedItemQuantityInCart
} from '@us/web/cart/data-access';
import { selectItemQuantityInStock } from '@us/web/inventory/data-access';
import { Food, ProductLayout } from '@us/web/shared/data-access';
import { NumberGroupComponent } from '@us/web/shared/ui';
import { isFnBApp, RouteUtil } from '@us/web/shared/utils';
import { FormatCurrencyPipe } from '@us/web/shop/data-access';
import { combineLatestWith, debounceTime, first, Observable, of, tap } from 'rxjs';
import { ProductItemModalComponent } from '../../components/product-item-modal/product-item-modal.component';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';

@UntilDestroy()
@Component({
    selector: 'us-product-item',
    templateUrl: './product-item.component.html',
    styleUrls: ['./product-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        NgOptimizedImage,
        TranslocoModule,
        CurrencyPipe,
        TuiButtonModule,
        FormatCurrencyPipe,
        AsyncPipe,
        TuiLetModule,
        NumberGroupComponent
    ],
    standalone: true
})
export class ProductItemComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly modalController = inject(ModalController);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly navController = inject(NavController);
    private readonly env = inject(ENVIRONMENT);
    private readonly dialogService = inject(TuiDialogService);
    private readonly injector = inject(Injector);

    @Input() item!: Food;
    @Input() layout: ProductLayout = ProductLayout.Grid;

    selectedQuantity$: Observable<number> = of(0);

    readonly ProductLayout = ProductLayout;
    readonly DEFAULT_IMAGE = '/assets/icons/picture.svg';

    quantityInStock = 0;
    minimumQuantity = 0;
    maximumQuantity: number = Number.MAX_VALUE;

    readonly IS_FNB_APP = isFnBApp(this.env);

    ngOnInit() {
        this.store
            .select(selectItemQuantityInStock(this.item.id))
            .pipe(
                untilDestroyed(this),
                combineLatestWith(this.store.select(selectItemQuantityInCart(this.item.id.toString())))
            )
            .subscribe(([quantityInStock, quantityInCart]) => {
                if (quantityInStock === -1) {
                    this.maximumQuantity = Number.MAX_VALUE;
                    this.quantityInStock = -1;
                } else {
                    const quantity = quantityInStock - quantityInCart;
                    this.maximumQuantity = quantity;
                    this.quantityInStock = quantity;
                }
                this.changeDetectorRef.markForCheck();
                this.changeDetectorRef.detectChanges();
            });

        this.selectedQuantity$ = this.store.select(selectSelectedItemQuantityInCart(this.item.id.toString()));
    }

    showProductDialog() {
        if (!this.item.available || this.quantityInStock === 0) {
            return;
        }
        this.dialogService
            .open<void>(new PolymorpheusComponent(ProductItemModalComponent, this.injector), {
                data: {
                    productId: this.item.id
                },
                dismissible: true,
                size: 'page'
            })
            .subscribe();
    }

    async addToCart(event: MouseEvent | null = null, quantity = 1) {
        event?.preventDefault();
        event?.stopPropagation();
        const food = this.item;

        if (food.units.length > 1 || food.options.length > 1) {
            await this.onProductClick();
            return;
        }

        const unit = food.units[0];
        const newCartItem: CartItem = {
            product_id: food.id.toString(),
            product_name: food.name,
            product_type: food.type,
            unit: unit,
            price: unit.price,
            quantity,
            note: '',
            version_code: 0,
            specifications: ''
        };

        this.store.dispatch(CartActions.addToCart({ item: newCartItem }));
    }

    decreaseQuantity() {
        this.store
            .select(selectItemInCart(this.item.id.toString()))
            .pipe(
                first(),
                tap(async items => {
                    if (items.length === 0) {
                        return;
                    }

                    if (items.length === 1) {
                        this.store.dispatch(
                            CartActions.updateItemQuantity({
                                item: items[0],
                                quantity: items[0].quantity - 1
                            })
                        );
                        return;
                    }
                    await this.navController.navigateForward([RouteUtil.getCartRouteUrl()]);
                })
            )
            .subscribe();
    }

    async increaseQuantity() {
        await this.addToCart();
    }

    async onProductClick() {
        if (isFnBApp(this.env)) {
            this.showProductDialog();
        } else {
            await this.navController.navigateForward(['products', this.item.id.toString()], {
                queryParams: {
                    pageYOffset: window.pageYOffset
                }
            });
        }
    }
}
